<template>
    <b-modal
        id="modal-integration-agents"
        shadow
        bg-variant="white"
        backdrop
        hide-footer
        centered
        size="lg"
        @hidden="onHidden"
    >
        <template #modal-header>
            <div class="d-flex align-items-center justify-content-start w-100 title-modal">
                <span class="modal-title">{{ $t("IntegrationKeys.AgentsTitle") }}</span>
            </div>
            <button
                type="button"
                aria-label="Close"
                class="close"
                @click="closeModal"
            >
                <CloseModalIcon />
            </button>
        </template>

        <div class="modal-integration-key__container">
            <div class="modal-agents-body">
                <p
                    v-if="keyDescription"
                    class="ik-agents-key-desc mb-0"
                >
                    {{ keyDescription }}
                </p>
                <b-alert
                    v-if="!agents || agents.length === 0"
                    show
                    variant="light"
                    class="mb-0 ik-agents-empty-alert"
                >
                    {{ $t("IntegrationKeys.NoAgents") }}
                </b-alert>
                <div
                    v-else
                    class="table-responsive ik-agents-table-wrap"
                >
                    <table class="table table-sm table-integration-agents mb-0">
                        <thead>
                            <tr>
                                <th>{{ $t("IntegrationKeys.DeviceId") }}</th>
                                <th>{{ $t("IntegrationKeys.MachineName") }}</th>
                                <th>{{ $t("IntegrationKeys.Version") }}</th>
                                <th>{{ $t("IntegrationKeys.IpAddress") }}</th>
                                <th>{{ $t("IntegrationKeys.LastContact") }}</th>
                                <th>{{ $t("IntegrationKeys.Status") }}</th>
                                <th>{{ $t("IntegrationKeys.AgentActive") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(agent, idx) in agents"
                                :key="agent.id || idx"
                            >
                                <td>{{ agent.deviceId }}</td>
                                <td>{{ agent.machineName }}</td>
                                <td>{{ agent.version }}</td>
                                <td>{{ agent.ipAddress }}</td>
                                <td>{{ formatDate(agent.lastContactAt) }}</td>
                                <td>{{ agent.status }}</td>
                                <td>
                                    <b-badge :variant="agent.active ? 'success' : 'secondary'">
                                        {{
                                            agent.active
                                                ? $t("IntegrationKeys.Active")
                                                : $t("IntegrationKeys.Inactive")
                                        }}
                                    </b-badge>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="modal-integration-key__footer d-flex justify-content-end flex-wrap">
                <sys-button
                    scheme="square"
                    variant="outline"
                    size="md"
                    @click="closeModal"
                >
                    {{ $t("IntegrationKeys.CloseConfirm") }}
                </sys-button>
            </div>
        </div>
    </b-modal>
</template>

<script>
    import { BModal, BAlert, BBadge } from "bootstrap-vue";
    import CloseModalIcon from "@/@core/assets/icons/close-modal-icon.svg";
    import SysButton from "@/@core/components/sys-button";
    import dayjs from "dayjs";

    export default {
        name: "ModalIntegrationAgents",
        components: {
            BModal,
            BAlert,
            BBadge,
            CloseModalIcon,
            SysButton
        },
        data() {
            return {
                agents: [],
                keyDescription: ""
            };
        },
        methods: {
            closeModal() {
                this.$bvModal.hide("modal-integration-agents");
            },
            onHidden() {
                this.agents = [];
                this.keyDescription = "";
            },
            formatDate(iso) {
                if (!iso) return "—";
                return dayjs(iso).format("DD/MM/YYYY HH:mm");
            },
            open(agents, description) {
                this.agents = Array.isArray(agents) ? [...agents] : [];
                this.keyDescription = description || "";
                this.$bvModal.show("modal-integration-agents");
            }
        }
    };
</script>

<style lang="scss">
    #modal-integration-agents {
        .modal-dialog {
            width: 95%;
            max-width: 900px;
        }

        .modal-content {
            width: 100%;
            right: 0 !important;
            border-radius: 6px;
            overflow: hidden;
            border: 1px solid #e0e0e0;
            box-shadow: 0px 8px 32px rgba(58, 46, 38, 0.12);
        }

        .modal-header {
            position: relative !important;
            display: flex;
            align-items: center;
            height: 66px;
            background-color: #eceff1;
            padding-top: 0;
            padding-bottom: 0;
            border-bottom: 1px solid #e0e0e0;

            .title-modal {
                height: 100%;
            }

            .modal-title {
                color: #4c4541;
                font-size: 18px;
                font-weight: 600;
                line-height: 26px;
            }

            .close {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                min-width: 28px;
                min-height: 28px;
                max-width: 28px;
                max-height: 28px;
                padding: 0;
                margin: 0;
                opacity: 1;
                transform: translate(6px, 5px);
                border: 1px solid #974900 !important;
                border-radius: 5px;
                background: transparent !important;
                box-shadow: none !important;

                svg {
                    min-height: 12px;
                    min-width: 12px;
                    max-width: 12px;
                    max-height: 12px;
                    fill: #974900 !important;
                }

                svg path {
                    fill: #974900 !important;
                }

                &:hover {
                    opacity: 1;
                    transform: translate(6px, 5px);
                    background-color: #ffede2 !important;
                }

                &:active {
                    background-color: #ffdbc4 !important;
                }

                &:focus {
                    outline: none;
                    box-shadow: 0 0 0 0.15rem rgba(151, 73, 0, 0.25);
                }
            }
        }

        .modal-body {
            padding: 0 !important;
        }

        .modal-integration-key__container {
            display: flex;
            flex-direction: column;
            padding: 20px 16px;
            gap: 20px;
            background-color: #fff;

            .modal-agents-body {
                display: flex;
                flex-direction: column;
                gap: 16px;
                min-width: 0;
            }

            .ik-agents-key-desc {
                font-size: 13px;
                line-height: 20px;
                color: #6b635e;
            }

            .ik-agents-empty-alert {
                background-color: #faf8f6;
                border-color: #e8e4e0;
                color: #4c4541;
                border-radius: 6px;
            }

            .ik-agents-table-wrap {
                max-height: min(55vh, 420px);
                overflow: auto;
                border: 1px solid #e8e4e0;
                border-radius: 6px;
            }

            .modal-integration-key__footer {
                display: flex;
                justify-content: flex-end;
                flex-wrap: wrap;
                margin-top: 0;
                gap: 8px;
            }

            .table-integration-agents {
                font-size: 13px;
                margin-bottom: 0;

                th {
                    white-space: nowrap;
                    background: rgba(151, 73, 0, 0.05);
                    color: #4c4541;
                    font-weight: 600;
                    font-size: 11px;
                    text-transform: uppercase;
                    border-color: #e8e4e0;
                }

                td {
                    vertical-align: middle;
                    color: #4c4541;
                    border-color: #e8e4e0;
                }
            }
        }
    }
</style>
